import React, { useEffect } from "react";
import { Flag, X } from "lucide-react";

export default function AddTaskModal({
    onClose,
    onSubmit,
    projects = [],
    fixedProjectId = null,
    fixedProjectName = '',
    initialData = null,
    isEditMode = false,
}) {
    const getInitialDeadline = (rawDeadline) => {
        if (!rawDeadline) return '';
        const date = new Date(rawDeadline);
        if (Number.isNaN(date.getTime())) return '';
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const [title, setTitle] = React.useState(initialData?.title ?? '');
    const [deadline, setDeadline] = React.useState(getInitialDeadline(initialData?.deadline));
    const [projectId, setProjectId] = React.useState(
        fixedProjectId != null ? String(fixedProjectId) : (initialData?.projectId != null ? String(initialData.projectId) : '')
    );
    const [repeat, setRepeat] = React.useState(initialData?.repeat ?? false);
    const [priority, setPriority] = React.useState(initialData?.priority ?? '');
    const isProjectLocked = fixedProjectId != null;

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    React.useEffect(() => {
        if (isProjectLocked) {
            setProjectId(String(fixedProjectId));
        }
    }, [fixedProjectId, isProjectLocked]);

    React.useEffect(() => {
        if (!initialData) return;
        setTitle(initialData.title ?? '');
        setDeadline(getInitialDeadline(initialData.deadline));
        setRepeat(initialData.repeat ?? false);
        setPriority(initialData.priority ?? '');
        if (!isProjectLocked) {
            setProjectId(initialData.projectId != null ? String(initialData.projectId) : '');
        }
    }, [initialData, isProjectLocked]);

    const priorityOptions = [
        { value: 'HIGH', label: 'High', colorClass: 'text-red-400' },
        { value: 'MEDIUM', label: 'Medium', colorClass: 'text-yellow-400' },
        { value: 'LOW', label: 'Low', colorClass: 'text-blue-400' },
    ];
    const getPriorityFlagClass = () => {
        if (priority === 'HIGH') return 'text-red-400';
        if (priority === 'MEDIUM') return 'text-yellow-400';
        if (priority === 'LOW') return 'text-blue-400';
        return 'text-gray-500';
    };
    const handleAddTask = () => {
        onSubmit({
            title,
            deadline,
            repeat,
            priority: priority || null,
            projectId: projectId.trim() ? projectId : null,
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 px-4" onClick={handleBackdropClick}>
            <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl shadow-xl p-6 w-full max-w-md relative">

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-200"
                >
                    <X size={20} />
                </button>

                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-100 mb-4">
                    {isEditMode ? 'Edit Task' : 'Add Task'}
                </h2>

                {/* Inputs */}
                <div className="flex flex-col gap-4">

                    {/* Task Title */}
                    <div className="flex flex-col gap-1">
                        <label className="text-gray-300 text-sm">Task Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter task"
                        />
                    </div>

                    {/* Deadline */}
                    <div className="flex flex-col gap-1">
                        <label className="text-gray-300 text-sm">Deadline</label>
                        <input
                            type="date"
                            value={deadline}
                            onChange={(e) => setDeadline(e.target.value)}
                            className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    {/* Priority */}
                    <div className="flex flex-col gap-1">
                        <label className="text-gray-300 text-sm flex items-center gap-2">
                            Priority
                            <Flag size={14} className={getPriorityFlagClass()} />
                        </label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="" className="text-gray-300">Priority</option>
                            {priorityOptions.map((option) => (
                                <option key={option.value} value={option.value} className={option.colorClass}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Project */}
                    <div className="flex flex-col gap-1">
                        <label className="text-gray-300 text-sm">Project</label>
                        <select
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            disabled={isProjectLocked}
                            className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isProjectLocked ? (
                                <option value={String(fixedProjectId)}>
                                    {fixedProjectName || 'Selected Project'}
                                </option>
                            ) : (
                                <>
                                    <option value="">No project</option>
                                    {projects.map((project) => (
                                        <option key={project.id ?? project.projectId} value={project.id ?? project.projectId ?? ''}>
                                            {project.name || 'Untitled Project'}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>

                    {/* Repeat */}
                    <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={repeat}
                            onChange={() => setRepeat(!repeat)}
                            className="accent-indigo-500"
                        />
                        Repeat
                    </label>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-[#2A2B32] transition"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleAddTask}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-500 transition"
                    >
                        {isEditMode ? 'Save Changes' : 'Add Task'}
                    </button>
                </div>
            </div>
        </div>
    );
}
